import { businessToday, isIsoDate, type IsoDate } from '@tumin/domain'
import { useBrandingStore } from '@/lib/branding'
import { LOCALE } from '@/lib/constants'

/**
 * Fechas en la zona horaria de la INSTITUCIÓN (settings.timeZone), no la del
 * dispositivo: un asesor con el teléfono mal configurado debe ver las mismas
 * fechas que la agencia.
 *
 * Dos tipos de valor:
 * - Instantes (`createdAt`, ISO con hora): se formatean en la zona de la institución.
 * - Fechas de calendario (`IsoDate` "AAAA-MM-DD": vencimientos, pagos): no
 *   tienen zona; se formatean tal cual, sin conversión.
 */

/** Zona horaria vigente de la institución. */
export function currentTimeZone(): string {
  return useBrandingStore.getState().branding.timeZone
}

/** Hook: zona horaria de la institución (re-renderiza si cambia). */
export function useOrgTimeZone(): string {
  return useBrandingStore((s) => s.branding.timeZone)
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options)
  let cached = formatterCache.get(key)
  if (!cached) {
    cached = new Intl.DateTimeFormat(LOCALE, options)
    formatterCache.set(key, cached)
  }
  return cached
}

type DateInput = string | number | Date | null | undefined

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Texto para valores ausentes en tablas y fichas. */
export const EMPTY_VALUE = '—'

/** Fecha de calendario "AAAA-MM-DD" → "31 jul 2026" (sin conversión de zona). */
export function formatIsoDate(value: string | null | undefined, style: 'short' | 'long' = 'short'): string {
  if (!value || !isIsoDate(value)) return EMPTY_VALUE
  return formatter({
    timeZone: 'UTC',
    day: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00Z`))
}

/** Instante → fecha en la zona de la institución: "31 jul 2026". */
export function formatDate(value: DateInput, timeZone: string = currentTimeZone()): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return formatter({ timeZone, day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

/** Instante → fecha y hora: "31 jul 2026, 14:05". */
export function formatDateTime(value: DateInput, timeZone: string = currentTimeZone()): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return formatter({ timeZone, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
}

/** Instante → hora "14:05". */
export function formatTime(value: DateInput, timeZone: string = currentTimeZone()): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  return formatter({ timeZone, hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
}

const relativeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

/** Umbrales para expresar tiempo relativo en la unidad más natural. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.35],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
]

/** Instante → tiempo relativo: "hace 5 minutos", "ayer". */
export function formatRelative(value: DateInput, now: Date = new Date()): string {
  const date = toDate(value)
  if (!date) return EMPTY_VALUE
  const MS_PER_SECOND = 1000
  let amount = (date.getTime() - now.getTime()) / MS_PER_SECOND
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(amount) < size) return relativeFormatter.format(Math.round(amount), unit)
    amount /= size
  }
  return formatDate(date)
}

/** Fecha de negocio de hoy en la institución ("AAAA-MM-DD"). */
export function orgToday(timeZone: string = currentTimeZone()): IsoDate {
  return businessToday(timeZone)
}

/** Zonas horarias IANA disponibles en el navegador (para el selector de la institución). */
export function availableTimeZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
  const zones = intl.supportedValuesOf?.('timeZone') ?? []
  return zones.length > 0 ? zones : ['America/Guatemala', 'America/Mexico_City', 'America/El_Salvador', 'America/Tegucigalpa', 'UTC']
}

/** Mes "AAAA-MM" → "jul 2026" (`short`) o "jul" (`tiny`, ejes de gráficas). */
export function formatMonth(value: string, style: 'short' | 'tiny' = 'short'): string {
  if (!/^\d{4}-\d{2}$/.test(value)) return value
  return formatter({ timeZone: 'UTC', month: 'short', ...(style === 'short' ? { year: 'numeric' } : {}) }).format(
    new Date(`${value}-01T00:00:00Z`),
  )
}
